import { ChatComposeBoxAddAttachmentMenuItem } from '@glade-ai/chat-plugin';
import { Icon } from '@glade-ai/ui';
import React, { type ComponentPropsWithoutRef, type FC, forwardRef, useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import SearchInput from '@/components/FormFields/SearchInput';
import ProgressIndicator from '@/components/ProgressIndicator';
import useNoodleApi from '@/hooks/useNoodleApi';
import TeamsContext from '@/providers/Teams/TeamsContext';
import * as tsClient from '@/tsClient';
import type * as ApiModels from '@/tsClient/servers/noodleDocuments/document-api-models';

import s from '../InputComposer.module.scss';

type Props = {
  disabled: boolean;
  onAttach: (data: { title: string; documentRequestTemplateId: string }) => void;
};

const ADD_DOCUMENT_REQUEST_LABEL = 'Document Request';
const ADD_DOCUMENT_REQUEST_ICON = 'ph--file-dashed--regular';

export const AddDocumentRequestMenuItem = forwardRef<
  HTMLDivElement,
  Omit<ComponentPropsWithoutRef<typeof ChatComposeBoxAddAttachmentMenuItem>, 'icon' | 'label'>
>((props, forwardedRef) => (
  <ChatComposeBoxAddAttachmentMenuItem
    {...props}
    ref={forwardedRef}
    icon={ADD_DOCUMENT_REQUEST_ICON}
    label={ADD_DOCUMENT_REQUEST_LABEL}
  />
));
AddDocumentRequestMenuItem.displayName = 'AddDocumentRequestMenuItem';

export const AddDocumentRequestPopoverContent: FC<Omit<Props, 'disabled'>> = ({ onAttach }) => {
  const { creatorId } = useContext(TeamsContext);
  const [search, setSearch] = useState('');
  const {
    data: documentRequestTemplates,
    getData: getDocumentRequestTemplates,
    fetchingState: { isFetching: isFetchingDocumentRequests },
  } = useNoodleApi(tsClient.documentRequests.getCreatorDocumentRequests);

  useEffect(() => {
    if (creatorId) {
      getDocumentRequestTemplates({ creatorId, page: 1, perPage: 1000 });
    }
  }, [creatorId, getDocumentRequestTemplates]);

  const handleClick = (template: Pick<ApiModels.DocumentRequest, 'id' | 'title'>): void => {
    onAttach({ documentRequestTemplateId: template.id, title: template.title });
  };

  if (documentRequestTemplates?.items.length === 0) {
    return null;
  }

  return (
    <>
      {!documentRequestTemplates || isFetchingDocumentRequests ? (
        <ProgressIndicator isCentered />
      ) : (
        <>
          <SearchInput
            label='Filter by title'
            placeholder='Type a title...'
            setSearch={setSearch}
            className={s.search}
          />
          {documentRequestTemplates.items
            .filter((template) => template.title.toLowerCase().includes(search.toLowerCase()))
            .map((template) => (
              <Buttons
                className={s.documentRequestButton}
                key={template.id}
                isWrapper
                isFlat
                onClick={() => handleClick(template)}
              >
                <Icon icon='ph--file-dashed--regular' size={3.5} />
                <p>
                  {template.title} ({template.files.length} document{template.files.length > 1 ? 's' : ''})
                </p>
              </Buttons>
            ))}
        </>
      )}
    </>
  );
};
